import { BRAND } from "constants/brand";
import "./addVendor.scss";
import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";

import { Ring } from "@uiball/loaders";
import { useNavigate } from "react-router-dom";
import useVendors from "hooks/useVendors";
import profileImage from "assets/images/profile.jpg";
import coverImage from "assets/images/notfound.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import useArea from "hooks/useArea";
import AreaCost from "./AreaCost/AreaCost";
import useVendorCategories from "hooks/useVendorCategories";
import Select from "components/Ui/SelectField/SelectField";
import useCity from "hooks/useCity";
import { readApiError } from "utils/apiError";
import { areaRowsProblem, newRowId, removeRow, toCostPayload, upsertRow } from "../areaCostRows";

const AddVendorPage = (props) => {
    const { loading, addVendor, error, addDeliveryCost } = useVendors();
    const { categories } = useVendorCategories();
    const { areas } = useArea();
    const [image, setImage] = useState(profileImage);
    const [cover, setCover] = useState(coverImage);
    const [city, setCity] = useState(null);
    // The ids of the area rows: see ../areaCostRows.js.
    const rowIdCounter = useRef(0);
    const [vendorAreas, setVendorArea] = useState([]);
    const [newCategories, setNewCategories] = useState([]);
    const [seletedCategories, setSelectedCategories] = useState([]);
    const form = useRef();
    const imageRef = useRef();
    const coverRef = useRef();
    const navigate = useNavigate();
    const { cities } = useCity();

    useEffect(() => {
        if (categories) {
            setNewCategories(
                categories.map((category) => {
                    return { value: category.id, label: category.name };
                }),
            );
        }
    }, [categories]);

    const getAreaCost = (data) => {
        setVendorArea((rows) => upsertRow(rows, data));
    };

    const handleChange = (selectedOptions) => {
        setSelectedCategories(selectedOptions);
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        // Before the restaurant is made: the API makes it in one call and its
        // delivery prices in a second one. A list that the API refuses must
        // not leave a restaurant behind.
        const rowsProblem = areaRowsProblem(vendorAreas, areas);
        if (rowsProblem) {
            props.showMessage("error", "هناك خطأ", rowsProblem);
            return;
        }

        const vendorData = new FormData();

        seletedCategories.forEach((category) => {
            vendorData.append("categories[]", category.value);
        });
        if (imageRef.current.value) {
            vendorData.append("image", imageRef.current.files[0], imageRef.current.files[0].name);
        }
        if (coverRef.current.value) {
            vendorData.append("cover", coverRef.current.files[0], coverRef.current.files[0].name);
        }

        // NAMED FIELDS, never positions. This form read `form.current[N]`, and
        // on 2026-10-09 the removal of the free delivery field (2 inputs)
        // moved each later value by two: the city went to `status`, and the
        // second password to `cityId`. No restaurant could be added until
        // 2026-10-18. The categories picker also has an input of its own.
        const field = (name) => form.current.querySelector(`[name="${name}"]`)?.value ?? "";
        vendorData.append("name", field("name"));
        vendorData.append("phone", field("phone"));
        vendorData.append("address", field("address"));
        vendorData.append("delivery_time", field("delivery_time"));
        // `direction` is the map link of the restaurant.
        vendorData.append("direction", field("direction"));
        // LEGACY: the order method is stored in users.email, as in EditVendor.
        vendorData.append("email", field("orderMethod"));
        vendorData.append("type", field("type"));
        vendorData.append("status", field("status"));
        vendorData.append("cityId", field("cityId"));
        vendorData.append("password", field("password"));
        vendorData.append("confirm_password", field("confirm_password"));

        try {
            const response = await addVendor(vendorData);
            const createFailure = readApiError(response, "تعذرت إضافة المطعم");
            if (createFailure || !response?.user?.id) {
                props.showMessage("error", "هناك خطأ", createFailure || "تعذرت إضافة المطعم");
                return;
            }

            const costsRes = await addDeliveryCost({
                vendorId: response.user.id,
                costs: vendorAreas.map(toCostPayload),
            });
            const costsFailure = readApiError(costsRes, "تعذر حفظ أسعار التوصيل");
            if (costsFailure || !costsRes?.results) {
                // THE RESTAURANT EXISTS NOW. A second press of the button made
                // it again: 6 times on 2026-10-06. So this form ends here, and
                // the admin corrects the prices in the page of the restaurant.
                props.showMessage(
                    "error",
                    "أُضيف المطعم، ولم تُحفظ أسعار التوصيل",
                    `${costsFailure || "تعذر حفظ أسعار التوصيل"} أضف مناطق التوصيل من هذه الصفحة.`,
                );
                navigate(`/vendors/${response.user.id}`);
                return;
            }

            props.showMessage("success", "تمت الاضافه", "تمت إضافه المطعم بنجاح");
            navigate("/vendors");
        } catch (err) {
            console.error("Error adding vendor:", err);
            props.showMessage("error", "هناك خطأ", error || "حدث خطأ غير متوقع");
        }
    };
    const deleteAreaHandler = async (id) => {
        setVendorArea((rows) => removeRow(rows, id));
    };

    return (
        <div className={`add-vendor`}>
            {loading ? (
                <div className="loading-spinner center">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : null}
            <h3 className="text-center" style={{ marginBottom: "20px" }}>
                إضافة مطعم
            </h3>
            <div className="images">
                <div className="image-input">
                    <FontAwesomeIcon
                        onClick={() => {
                            setImage(profileImage);
                            imageRef.current.value = "";
                        }}
                        icon={faTrashCan}
                        size="lg"
                        color="red"
                    />
                    <label htmlFor="image">
                        <img src={image} alt="profile" />
                    </label>
                    <input
                        type="file"
                        ref={imageRef}
                        onChange={(e) => {
                            if (e.target.files[0]) {
                                setImage(URL.createObjectURL(e.target.files[0]));
                            }
                        }}
                        accept=".png,.jpg,.jpeg,.svg,.webp"
                        name="image"
                        id="image"
                    />
                </div>
                <div className="image-input">
                    <FontAwesomeIcon
                        onClick={() => {
                            setCover(coverImage);
                            coverRef.current.value = "";
                        }}
                        icon={faTrashCan}
                        size="lg"
                        color="red"
                    />
                    <label htmlFor="cover">
                        <img src={cover} alt="cover" />
                    </label>
                    <input
                        ref={coverRef}
                        type="file"
                        onChange={(e) => {
                            if (e.target.files[0]) {
                                setCover(URL.createObjectURL(e.target.files[0]));
                            }
                        }}
                        accept=".png,.jpg,.jpeg,.svg,.webp"
                        name="cover"
                        id="cover"
                    />
                </div>
            </div>
            <form ref={form} className="row row-cols-1" onSubmit={submitHandler}>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="name">
                        <Form.Label>
                            اسم المطعم<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control name="name" type="text" placeholder="اسم المطعم" required />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="phone">
                        <Form.Label>
                            رقم الهاتف<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control name="phone" type="text" placeholder="رقم الهاتف" required />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="address">
                        <Form.Label>
                            العنوان<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control name="address" type="text" placeholder="العنوان" required />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="phone">
                        <Form.Label>
                            وقت التوصيل<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="delivery_time"
                            type="number"
                            placeholder="وقت التوصيل"
                            min={0}
                            defaultValue={0}
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="address">
                        <Form.Label>
                            رابط الخريطه<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="direction"
                            type="text"
                            placeholder="رابط الخريطه"
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="type">
                        <Form.Label>
                            طريقه الطلب<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Select name="orderMethod" required>
                            <option value={"cart"}>السله</option>
                            <option value={"phone"}>الهاتف</option>
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="status">
                        <Form.Label>
                            النوع<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Select name="type" required>
                            <option value={"restaurant"}>مطعم</option>
                            <option value={"supermarket"}>سوبر ماركت</option>
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="status">
                        <Form.Label>
                            الحاله<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Select name="status" required>
                            <option value={"close"}>مغلق</option>
                            <option value={"open"}>مفتوح</option>
                            <option value={"soon"}>قريبا</option>
                            <option value={"busy"}>مشغول</option>
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="status">
                        <Form.Label>
                            التصنيف<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Select
                            value={seletedCategories}
                            options={newCategories}
                            isMulti
                            onChange={handleChange}
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="status">
                        <Form.Label>
                            المدينه<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Select
                            name="cityId"
                            required
                            onChange={(e) => {
                                setCity(e.target.value);
                                // The rows have areas of the city before.
                                setVendorArea([]);
                            }}
                        >
                            <option value={""}>-----</option>
                            {cities?.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="password">
                        <Form.Label>
                            كلمه المرور<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="password"
                            type="password"
                            placeholder="كلمه المرور"
                            required
                        />
                    </Form.Group>
                </div>
                <div className="col-md-4 col-lg-4">
                    <Form.Group className="mb-3" controlId="confirm-password">
                        <Form.Label>
                            تأكيد كلمه المرور<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            name="confirm_password"
                            type="password"
                            placeholder="تأكيد كلمه المرور"
                            required
                        />
                    </Form.Group>
                </div>
                <div>
                    <button
                        style={{ margin: "40px 0 0 10px", minWidth: "fit-content" }}
                        className="button"
                        onClick={() => {
                            // No area yet: the admin selects it. The row
                            // held area 1 before, whatever the list showed.
                            setVendorArea((pre) => [
                                ...pre,
                                {
                                    id: newRowId(rowIdCounter),
                                    area: 0,
                                    cost: 0,
                                    available: true,
                                },
                            ]);
                        }}
                        type="button"
                    >
                        إضافه منطقه
                    </button>
                </div>

                {vendorAreas.map((item) => (
                    <AreaCost
                        key={item.id}
                        index={item.id}
                        getAreaCost={getAreaCost}
                        areas={
                            city === "" || !city
                                ? areas
                                : areas.filter((area) => +area.cityId === +city)
                        }
                        deleteArea={deleteAreaHandler}
                    />
                ))}
                <div className="submit">
                    <button
                        style={{
                            marginTop: "20px",
                            width: "250px",
                            fontWeight: "bold",
                            float: "left",
                        }}
                        className="button"
                        type="submit"
                    >
                        إضافه المطعم
                    </button>
                </div>
            </form>
        </div>
    );
};
export default AddVendorPage;
